import type { FCWithChildren } from '@pbk-web/types';
import type { ReactElement } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import Toast from '../components/Toast';

export enum ToastType {
  INFO,
  ERROR,
  SUCCESS,
  WARNING,
}

export type ToastDto = {
  type: ToastType;
  title: string | ReactElement;
  description?: string | ReactElement;
  expiresInMs?: number;
  slots?: {
    button?: ReactElement;
  };
};

export type ToastConfig = ToastDto & {
  id: string;
};

export type ToastContextType = {
  addToast: (toast: ToastDto) => string;
  removeToast: (id: string) => void;
  hasToast: (id: string) => boolean;
};

export const ToastContext = createContext<ToastContextType>({
  addToast: () => {
    throw new Error('NO TOAST PROVIDER!');
  },
  removeToast: () => {
    throw new Error('NO TOAST PROVIDER!');
  },
  hasToast: () => {
    throw new Error('NO TOAST PROVIDER!');
  },
});
export const useToasts = (): ToastContextType => useContext(ToastContext);
export const ToastProvider: FCWithChildren = ({ children }) => {
  const [toasts, setToasts] = useState<ToastConfig[]>([]);

  const addToast = useCallback((toast: ToastDto): string => {
    const id = uuid();
    setToasts((prev) => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const hasToast = useCallback(
    (id: string) => {
      return toasts.some((toast) => toast.id === id);
    },
    [toasts],
  );

  const providerValues = useMemo(() => ({ addToast, removeToast, hasToast }), [addToast, removeToast, hasToast]);

  return (
    <>
      <div className="z-toast fixed top-0 right-0 left-0 mx-auto mt-6 w-full max-w-lg md:right-4 md:left-auto md:mr-4 md:max-w-lg lg:max-w-lg">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} removeToast={removeToast}>
            {toast.slots?.button && <Toast.Slot name="button">{toast.slots.button}</Toast.Slot>}
          </Toast>
        ))}
      </div>
      <ToastContext.Provider value={providerValues}>{children}</ToastContext.Provider>
    </>
  );
};
